import React from "react";
import PropTypes from "prop-types";
import { Box, Chip, Typography } from "@mui/material";
import Widget from "../Common/Widget/Widget";
import { AQI_SCALE_BANDS } from "../../constants/smartRoomConstants";
import {
  formatDisplayValue,
  getAqiBand,
} from "../../utils/smartRoomStatusRanges";
import mascotGood from "../../assets/aqi-mascots/aqi-mascot-good.png";
import mascotModerate from "../../assets/aqi-mascots/aqi-mascot-moderate.png";
import mascotPoor from "../../assets/aqi-mascots/aqi-mascot-poor.png";
import mascotUnhealthy from "../../assets/aqi-mascots/aqi-mascot-unhealthy.png";
import mascotSevere from "../../assets/aqi-mascots/aqi-mascot-severe.png";
import mascotHazardous from "../../assets/aqi-mascots/aqi-mascot-hazardous.png";

const MASCOT_BY_BAND = {
  good: mascotGood,
  moderate: mascotModerate,
  poor: mascotPoor,
  unhealthy: mascotUnhealthy,
  severe: mascotSevere,
  hazardous: mascotHazardous,
};

const MASCOT_ALT = {
  good: "Happy air quality mascot",
  moderate: "Moderate air quality mascot",
  poor: "Poor air quality mascot",
  unhealthy: "Unhealthy air quality mascot",
  severe: "Severe air quality mascot",
  hazardous: "Hazardous air quality mascot",
};

const getMascotSrc = (bandKey) =>
  MASCOT_BY_BAND[bandKey] || MASCOT_BY_BAND.good;

const AqiSummaryCard = ({ aqiValue, lastUpdatedLabel }) => {
  const band = getAqiBand(aqiValue);
  const display = formatDisplayValue(aqiValue, 0);
  const pointerPct =
    aqiValue == null
      ? 0
      : Math.min(100, Math.max(0, (Number(aqiValue) / 500) * 100));
  const mascotSrc = getMascotSrc(band?.key);
  const isModerate = band?.key === "moderate";

  return (
    <Widget
      title="Indoor"
      subheader="Air Quality Index"
      disableHeaderDivider
      sx={{
        bgcolor: "#fff",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        border: "1px solid",
        borderColor: "rgba(15, 23, 42, 0.06)",
      }}
      headerSx={{
        pb: 0.5,
        "& .MuiCardHeader-title": {
          fontSize: 13,
          fontWeight: 600,
          color: "text.secondary",
        },
        "& .MuiCardHeader-subheader": {
          fontSize: 16,
          fontWeight: 700,
          color: "text.primary",
          mt: 0.25,
        },
      }}
      contentSx={{ pt: 0.5 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2.5,
          minHeight: 120,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 52, md: 64 },
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.03em",
              color: "text.primary",
            }}
          >
            {display}
          </Typography>
          <Chip
            label={(band?.label || "—").toUpperCase()}
            size="small"
            sx={{
              mt: 1.25,
              height: 28,
              fontWeight: 800,
              letterSpacing: 0.6,
              bgcolor: band?.color || "#9E9E9E",
              color: isModerate ? "#333" : "#fff",
              borderRadius: 1.5,
              px: 0.75,
              boxShadow: `0 6px 14px ${band?.color || "#9E9E9E"}55`,
            }}
          />
        </Box>

        <Box
          component="img"
          src={mascotSrc}
          alt={MASCOT_ALT[band?.key] || "Air quality mascot"}
          sx={{
            width: { xs: 104, sm: 120, md: 132 },
            height: { xs: 104, sm: 120, md: 132 },
            objectFit: "contain",
            flexShrink: 0,
            userSelect: "none",
            pointerEvents: "none",
            backgroundColor: "transparent",
          }}
        />
      </Box>

      <Box sx={{ position: "relative", mb: 1.25, px: 0.25 }}>
        <Box
          sx={{
            display: "flex",
            height: 12,
            borderRadius: 999,
            overflow: "hidden",
            boxShadow: "inset 0 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          {AQI_SCALE_BANDS.map((item) => (
            <Box
              key={item.key}
              sx={{
                flex: item.max - item.min + 1,
                bgcolor: item.color,
              }}
            />
          ))}
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: -5,
            left: `calc(${pointerPct}% - 7px)`,
            width: 14,
            height: 14,
            borderRadius: "50%",
            bgcolor: "#fff",
            border: "3px solid",
            borderColor: "text.primary",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 1,
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          {AQI_SCALE_BANDS.map((item) => (
            <Typography
              key={item.key}
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: 10,
                fontWeight: band?.key === item.key ? 700 : 500,
              }}
            >
              {item.label}
            </Typography>
          ))}
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        Last Updated: {lastUpdatedLabel || "—"}
      </Typography>
    </Widget>
  );
};

AqiSummaryCard.propTypes = {
  aqiValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lastUpdatedLabel: PropTypes.string,
};

export default AqiSummaryCard;
